/**
 * Утилита для структурированного логирования в приложении
 * Все логи имеют префикс [ComponentName] для удобной фильтрации
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private componentName: string;
  private isDevelopment: boolean;

  constructor(componentName: string) {
    this.componentName = componentName;
    this.isDevelopment = import.meta.env.DEV;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${this.componentName}]`;
    return `${prefix} [${timestamp}] [${level.toUpperCase()}] ${message}`;
  }

  private log(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    // В production оставляем только ошибки, чтобы не засорять консоль/логи
    if (!this.isDevelopment && level !== 'error') return;

    const formattedMessage = this.formatMessage(level, message, context);
    const logData: any[] = [formattedMessage];

    if (context && Object.keys(context).length > 0) {
      logData.push(context);
    }

    if (error) {
      logData.push(error);
      if (error.stack) {
        logData.push({ stack: error.stack });
      }
    }

    switch (level) {
      case 'debug':
        console.debug(...logData);
        break;
      case 'info':
        console.log(...logData);
        break;
      case 'warn':
        console.warn(...logData);
        break;
      case 'error':
        console.error(...logData);
        break;
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: LogContext) {
    this.log('error', message, context, error);
  }

  // Специальные методы для типичных случаев
  componentMount(props?: any) {
    this.info('Component mounted', props ? { props } : undefined);
  }

  componentUnmount() {
    this.info('Component unmounting');
  }

  stateChange(stateName: string, oldValue: any, newValue: any) {
    this.debug(`State changed: ${stateName}`, {
      oldValue,
      newValue,
    });
  }

  propChange(propName: string, oldValue: any, newValue: any) {
    this.debug(`Prop changed: ${propName}`, {
      oldValue,
      newValue,
    });
  }

  apiCall(method: string, endpoint: string, params?: any) {
    this.info(`API call: ${method} ${endpoint}`, params);
  }

  apiSuccess(method: string, endpoint: string, data?: any) {
    this.info(`API success: ${method} ${endpoint}`, data ? { dataLength: Array.isArray(data) ? data.length : 'object' } : undefined);
  }

  apiError(method: string, endpoint: string, error: any) {
    this.error(`API error: ${method} ${endpoint}`, error instanceof Error ? error : undefined, {
      status: error?.status,
      message: error?.message,
      code: error?.code,
    });
  }

  userAction(action: string, details?: LogContext) {
    this.info(`User action: ${action}`, details);
  }

  render(phase: 'start' | 'end', details?: LogContext) {
    this.debug(`Render ${phase}`, details);
  }
}

/**
 * Создает логгер для компонента
 */
export function createLogger(componentName: string): Logger {
  return new Logger(componentName);
}

/**
 * Хук для использования логгера в компонентах
 */
export function useLogger(componentName: string): Logger {
  return new Logger(componentName);
}

